import { Redirect, useParams } from "react-router-dom";

import { adminRoute } from "@/config/roles";
import { isCseProfile } from "@/core/helpers/(predicates)/adminProfile";
import { useCanGrantLcsw } from "@/features/permissions/hooks/useCanGrantLcsw";
import { useCurrentUserId } from "@/lib/hooks/useCurrentUserId";
import { useUserPermissions } from "@/features/permissions/api/queries/useUserPermissions";
import type { UUID } from "@/core/types";

import { ProfileSidebar } from "../../(shared)/(profile)/components/ProfileSidebar";
import { UserProfile } from "../../(shared)/(profile)/UserProfile";

import { AdminCalendarPreferencesCard } from "./components/AdminCalendarPreferencesCard";
import { BasicInfoCard } from "./components/BasicInfoCard";
import { LcswPermissionOthers } from "./components/lcsw/LcswPermissionOthers";
import { useAdminProfileById } from "./api/queries/useAdminProfileById";

export function AdminProfileOthers() {
  const { adminId } = useParams<{ adminId: UUID }>();
  const currentUserId = useCurrentUserId();
  const { data: profileData, isLoading: isProfileLoading } = useAdminProfileById(adminId);
  const isCse = isCseProfile(profileData);

  const { canGrantLcsw, isLoading: isCanGrantLoading } = useCanGrantLcsw({ enabled: isCse });
  const { data: permissions, isLoading: isPermissionsLoading } = useUserPermissions({
    enabled: isCse,
    userId: adminId,
  });

  // viewing own profile via the "others" route -> send to the editable self view
  if (adminId === currentUserId) return <Redirect to={`${adminRoute}/profile`} />;

  const isLcswDataLoading = isCse && (isCanGrantLoading || isPermissionsLoading);
  const isLoading = isProfileLoading || !profileData || isLcswDataLoading;

  return (
    <UserProfile
      asideContent={profileData ? <ProfileSidebar profileData={profileData} /> : null}
      isLoading={isLoading}
      renderMainContent={() =>
        profileData ? (
          <>
            <BasicInfoCard isReadOnly profileData={profileData} />
            <LcswPermissionOthers
              canGrantLcsw={canGrantLcsw}
              permissions={permissions}
              profileData={profileData}
              userId={adminId}
            />
            <AdminCalendarPreferencesCard isReadOnly preferences={profileData.preferences} />
          </>
        ) : null
      }
      title={profileData?.name ?? "Admin Profile"}
    />
  );
}
